"use client";

import { motion, useInView, useReducedMotion } from "framer-motion";
import type { PropsWithChildren } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMotionReady } from "@/hooks/useMotionReady";

interface StaggerProps extends PropsWithChildren {
  staggerDelay?: number;
  initialDelay?: number;
  className?: string;
  /** Equivalent of viewport.amount (0..1). Lower triggers earlier. */
  amount?: number;
  /** Trigger only once */
  once?: boolean;
}

export default function Stagger({
  children,
  staggerDelay = 0.1,
  initialDelay = 0,
  className = "",
  amount = 0.1,
  once = true,
}: StaggerProps) {
  const reduce = useReducedMotion();
  const ready = useMotionReady();
  const motionEnabled = ready && !reduce;
  const [shown, setShown] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(ref, {
    once,
    amount,
    // Slight margin helps iOS trigger more consistently near edges
    margin: "0px 0px -5% 0px",
  });

  useEffect(() => {
    if (motionEnabled && isInView) {
      setShown(true);
    }
  }, [isInView, motionEnabled]);

  useEffect(() => {
    if (!motionEnabled || shown || !once) {
      return;
    }

    const timer = window.setTimeout(() => {
      setShown(true);
    }, 600);

    return () => window.clearTimeout(timer);
  }, [motionEnabled, shown, once]);

  const variants = useMemo(
    () => ({
      hidden: {},
      visible: {
        transition: {
          delayChildren: initialDelay,
          staggerChildren: staggerDelay,
        },
      },
    }),
    [initialDelay, staggerDelay]
  );

  const shouldShow = once ? shown : isInView;

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={motionEnabled ? "hidden" : false}
      animate={motionEnabled ? (shouldShow ? "visible" : "hidden") : "visible"}
      variants={variants}
      // Helps mobile Safari render transform animations smoothly
      style={{
        willChange: "transform, opacity",
        transform: "translateZ(0)",
      }}
    >
      {children}
    </motion.div>
  );
}

export const StaggerItem = ({ children }: PropsWithChildren) => {
  const reduce = useReducedMotion();
  const ready = useMotionReady();
  const motionEnabled = ready && !reduce;

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 },
      }}
      transition={
        motionEnabled ? { duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] } : undefined
      }
      style={{
        willChange: "transform, opacity",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
      }}
    >
      {children}
    </motion.div>
  );
};
